import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { signOut } from '../redux/actions';
import { Link } from 'react-router-dom';
import { addReaderToConnectedAccountIfNeeded, cancelPOSPaymentAction } from '../redux/actions';
import LogoSection from '../components/LogoSection';

const Settings = () => {
    const dispatch = useDispatch();
    const [stripeTerminalCode, setStripeTerminalCode] = useState('');
    const [isTerminalOpen, setIsTerminalOpen] = useState(false);
    const clientAccountId = useSelector((state) => state.clientAccountId);
    const readers = useSelector((state) => state.readers);
    const awaitingReaderResponse = useSelector((state) => state.awaitingReaderResponse);
    const userName = useSelector((state) => state.userDetails.displayName);
    const userEmail = useSelector((state) => state.userDetails.email);
    const userRole = useSelector((state) => state.userRole);
    const tenantID = useSelector((state) => state.userTenant);

    const handleSignOut = () => {
        dispatch(signOut());
    };

    const handleAddReader = () => {
        dispatch(addReaderToConnectedAccountIfNeeded(stripeTerminalCode, clientAccountId));
    };

    const handleCancelPOS = () => {
        dispatch(cancelPOSPaymentAction());
    };

    const isAdmin = userRole === 'admin';
    const isTenantAdmin = userRole === 'tenant-admin';
    const canManagePOS = isAdmin || isTenantAdmin;

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <LogoSection />

                {/* User Info Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Name</p>
                            <p className="text-base font-medium text-gray-900">{userName || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Email</p>
                            <p className="text-base font-medium text-gray-900">{userEmail || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Role</p>
                            <p className="text-base font-medium text-gray-900 capitalize">{userRole || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 mb-1">Tenant ID</p>
                            <p className="text-base font-medium text-gray-900">{tenantID || 'N/A'}</p>
                        </div>
                    </div>
                </div>

                {/* Sign Out Button */}
                <div className="mb-6">
                    <button
                        onClick={handleSignOut}
                        className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-medium text-base"
                    >
                        Sign Out
                    </button>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                    <div className="space-y-3">
                    <Link
                        to="/"
                            className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                    >
                        Payments Page
                    </Link>
                    <Link
                        to="/completed-transactions"
                            className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                    >
                        View Completed Transactions
                        </Link>
                        {isAdmin && (
                            <Link
                                to="/user-logs"
                                className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                            >
                                User Logs
                            </Link>
                        )}
                    </div>
                </div>

                {/* POS Administration Section (Admin and Tenant-Admin) */}
                {canManagePOS && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">POS Administration</h2>
                        <div className="space-y-3">
                            <Link
                                to="/pos-admin"
                                className="block w-full px-4 py-3 bg-blue-100 text-blue-900 rounded-lg hover:bg-blue-200 transition-colors duration-200 text-center font-medium"
                            >
                                POS Admin (Locations, Readers, Targets)
                            </Link>
                            <Link
                                to="/users"
                                className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                            >
                                Manage Users
                            </Link>
                        </div>
                    </div>
                )}

                {/* Manage Tenants Section (Admin Only) */}
                {isAdmin && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Administration</h2>
                        <div className="space-y-3">
                            <Link
                                to="/manage-targets"
                                className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                            >
                                Manage Tenants & Targets
                            </Link>
                            <Link
                                to="/delete-requests"
                                className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                            >
                                Delete Requests
                            </Link>
                        </div>
                    </div>
                )}

                {/* Stripe Terminal (Physical) Section - Accordion at Bottom */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <button
                        onClick={() => setIsTerminalOpen(!isTerminalOpen)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                        <h2 className="text-lg font-semibold text-gray-900">Stripe Terminal (Physical)</h2>
                        <svg
                            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isTerminalOpen ? 'transform rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {isTerminalOpen && (
                        <div className="px-6 pb-6 space-y-4 border-t border-gray-200 pt-4">
                            <Link
                                to="/pos-terminal-setup"
                                className="block w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-center font-medium"
                            >
                                WisePOS Terminal Setup
                            </Link>
                            
                            <button
                                onClick={handleCancelPOS}
                                className="w-full px-4 py-3 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                            >
                                Cancel POS Payment
                            </button>
                            
                            <div>
                                <label htmlFor="stripeTerminalCode" className="block text-sm font-medium text-gray-700 mb-2">
                                    Stripe Terminal Code
                                </label>
                                <input
                                    type="text"
                                    id="stripeTerminalCode"
                                    name="stripeTerminalCode"
                                    value={stripeTerminalCode}
                                    onChange={(e) => setStripeTerminalCode(e.target.value)}
                                    placeholder="Enter terminal code"
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition-all duration-200"
                                />
                            </div>
                            
                            <button
                                onClick={handleAddReader}
                                disabled={awaitingReaderResponse}
                                className={`w-full px-4 py-3 rounded-lg font-medium transition-colors duration-200 ${
                                    awaitingReaderResponse
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-gray-900 text-white hover:bg-gray-800'
                                }`}
                            >
                                {awaitingReaderResponse ? 'Connecting...' : 'Connect Terminal'}
                            </button>
                            
                            {readers.length > 1 && (
                                <div className="pt-2 border-t border-gray-200">
                                    <p className="text-sm text-gray-600 text-center">
                                        <span className="font-medium">Connected readers:</span> {readers.length}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;
